import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/ui-kit";
import { fmtTime } from "@/components/Pager";
import { toast } from "sonner";
import { Bus, Phone, MapPin, Clock } from "lucide-react";
import { STATUS, card, usePoll, LocationNote, BusMap } from "./common";

function Contact({ who, p }) {
  if (!p) return <div className="text-sm text-slate-400">{who}: not assigned</div>;
  return (
    <div className="text-sm">{who}: <b>{p.name}</b> <a data-testid={`call-${who.toLowerCase()}`} href={`tel:${p.phone}`} className="inline-flex items-center gap-1 text-violet-700 ml-1"><Phone size={13} /> {p.phone}</a></div>
  );
}

function Eta({ eta, id }) {
  if (!eta) return null;
  const text = eta.status === "ok" ? null : eta.status === "done" ? "Already marked on this trip" : eta.status === "no_gps" ? "Arrival time unavailable — no live GPS right now" : "Arrival time unavailable — stop location not set";
  if (text) return <div data-testid={`eta-${id}`} className="text-sm text-slate-500">{text}</div>;
  return (
    <div data-testid={`eta-${id}`} className="flex items-center gap-3 rounded-xl bg-violet-50 border border-violet-100 px-4 py-3">
      <Clock size={22} className="text-violet-700" />
      <div><div className="font-display text-lg font-bold text-violet-800">Bus arrives in about {eta.minutes} min</div>
        <div className="text-xs text-slate-500">~{eta.km} km away • estimate, may change with traffic</div></div>
    </div>
  );
}

function ChildCard({ c }) {
  if (!c.assigned) {
    return <div data-testid={`bus-child-${c.student.id}`} className={card}><div className="font-display font-bold">{c.student.name}</div><div className="text-sm text-slate-500 mt-1">Not using school transport.</div></div>;
  }
  const loc = c.vehicle?.last_location;
  return (
    <div data-testid={`bus-child-${c.student.id}`} className={`${card} space-y-3`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display font-bold text-slate-900">{c.student.name}</span>
        <span className="text-xs text-slate-500">{c.student.class_name}-{c.student.section}</span>
        {c.trip ? <Badge data-testid="trip-running" className="bg-emerald-100 text-emerald-800 ml-auto">{c.trip.kind === "pickup" ? "Pickup" : "Drop"} trip running</Badge>
          : <Badge className="bg-slate-100 text-slate-600 ml-auto">No trip running</Badge>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm"><Bus size={15} className="text-violet-700" /> Bus <b>{c.vehicle?.reg_no || "not assigned"}</b> {c.vehicle?.model && <span className="text-slate-400">{c.vehicle.model}</span>}</div>
          <div className="flex items-center gap-2 text-sm"><MapPin size={15} className="text-violet-700" /> {c.route?.name} • stop <b>{c.stop?.name || "—"}</b></div>
          <div className="flex items-center gap-2 text-sm"><Clock size={15} className="text-violet-700" /> Pickup {c.stop?.pickup_time || "—"} • Drop {c.stop?.drop_time || "—"}</div>
        </div>
        <div className="space-y-1"><Contact who="Driver" p={c.driver} /><Contact who="Attendant" p={c.attendant} /></div>
      </div>
      {c.today?.length > 0 && <div className="flex flex-wrap gap-2">{c.today.map((m, i) => <Badge key={i} className={STATUS[m.status][1]}>{STATUS[m.status][0]} • {fmtTime(m.at)}</Badge>)}</div>}
      {c.trip && <Eta eta={c.eta} id={c.student.id} />}
      <LocationNote loc={loc} testid={`bus-loc-${c.student.id}`} />
      {c.vehicle && <BusMap testid={`bus-map-${c.student.id}`} height={260} buses={[{ id: c.student.id, label: `Bus ${c.vehicle.reg_no}`, loc }]} stops={c.stop ? [c.stop] : []} />}
    </div>
  );
}

export function ParentTransport() {
  const [items, setItems] = useState(null);
  usePoll(() => api.get("/transport/child").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e))), 15000);
  if (!items) return <div className="h-40 rounded-2xl bg-white animate-pulse" />;
  if (!items.length) return <Empty testid="bus-empty" icon={Bus} text="No children linked to your account." />;
  return <div className="space-y-4">{items.map((c) => <ChildCard key={c.student.id} c={c} />)}</div>;
}
